import { useEffect, useState, useRef } from "react";
import { Grid, GridColumn } from '@progress/kendo-react-grid';
import { ExcelExport } from '@progress/kendo-react-excel-export';
import { process } from '@progress/kendo-data-query';
import { PrivateAxios, url } from "../../environment/AxiosInstance";
import TargetModal from "./TargetModal";
import EditTargetModal from "./EditTargetModal";
import ChartTargets from "./ChartTargets";
import { CurrencyList } from '../../utils/StaticData';
import { isURL } from '../../environment/Helper';
import moment from "moment";
import '@progress/kendo-theme-default/dist/all.css';
import './SalesTargets.css';
import { UserAuth } from '../auth/Auth';

export default function ViewTargets() {
  const { MatchPermission } = UserAuth();

  // ================== ALL HOOKS MUST BE AT THE TOP ==================
  const [targets, setTargets] = useState([]);
  const [filteredTargets, setFilteredTargets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedTarget, setSelectedTarget] = useState(null);
  const [editTarget, setEditTarget] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [summary, setSummary] = useState({});
  const [period, setPeriod] = useState('monthly');
  const [dataState, setDataState] = useState({
    skip: 0,
    take: 10,
    sort: []
  });
  const excelExportRef = useRef();

  useEffect(() => {
    // Only fetch targets if user has permission
    if (MatchPermission(['Sales All', 'View Sales', 'Edit Sales', 'Add Sales', 'Doer Edit Sales'])) {
      fetchTargets();
    }
  }, [MatchPermission]);

  // ================== PERMISSION CHECK AFTER ALL HOOKS ==================
  if (!MatchPermission(['Sales All', 'View Sales', 'Edit Sales', 'Add Sales', 'Doer Edit Sales'])) {
    return (
      <div className="p-4 text-center">
        <h4>Access Denied</h4>
        <p>You don't have permission to view sales targets.</p>
      </div>
    );
  }

  const fetchTargets = async () => {
    setLoading(true);
    try {
      const res = await PrivateAxios.get("/sales-targets");
      console.log("API Response:", res.data);

      const targetsData = Array.isArray(res.data?.data) ? res.data.data :
        Array.isArray(res.data) ? res.data : [];

      setTargets(targetsData);
      setFilteredTargets(targetsData);

      // Calculate summary from targets data
      const calculatedSummary = calculateSummary(targetsData);
      setSummary(res.data?.summary || calculatedSummary);
    } catch (err) {
      console.error("Fetch targets error:", err);
      setTargets([]);
      setFilteredTargets([]);
      setSummary({});
    } finally {
      setLoading(false);
    }
  };

  // Summary must match the values shown in the grid: each row's "Target Amount"
  // is the sum of per-assignee `assigned_amount` (or `assigned_number` for
  // number targets), NOT the parent target.target_amount. Previously the
  // summary used target.target_amount which diverges from the grid whenever
  // per-assignee amounts have been edited or when multiple assignees each
  // received the full target amount at creation time.
  const calculateSummary = (targetsData) => {
    let totalTarget = 0;
    let totalAchievement = 0;

    targetsData.forEach(target => {
      const targetType = target.target_type || 'amount';
      const assignees = Array.isArray(target.assignees) ? target.assignees : [];

      if (targetType === 'number') {
        assignees.forEach(a => {
          totalTarget += Number(a.assigned_number) || 0;
          totalAchievement += Number(a.number_ach) || 0;
        });
      } else {
        assignees.forEach(a => {
          totalTarget += Number(a.assigned_amount) || 0;
          totalAchievement += (Number(a.rnc_ach) || 0) + (Number(a.rec_ach) || 0);
        });
      }
    });

    return {
      total_target: totalTarget,
      total_achievement: totalAchievement
    };
  };

  const handleDataStateChange = (event) => {
    setDataState(event.dataState);
  };

  const AssigneesCell = (props) => {
    const assignees = props.dataItem.assignees || [];
    return (
      <td>
        <div className="d-flex flex-wrap gap-1">
          {assignees.slice(0, 2).map((assignee, index) => (
            <div key={index} className="d-flex align-items-center assignee-item">
              <div className="assignee-avatar">
                {assignee.assignee?.user_photo && isURL(assignee.assignee.user_photo) ? (
                  <img
                    src={`${url}${assignee.assignee.user_photo}`}
                    alt={assignee.assignee?.name || 'User'}
                    className="rounded-circle"
                    style={{ width: '20px', height: '20px' }}
                  />
                ) : (
                  <div className="avatar-placeholder">
                    {(assignee.assignee?.name || 'U').charAt(0).toUpperCase()}
                  </div>
                )}
              </div>
              <span className="assignee-name text-truncate">
                {assignee.assignee?.name || 'Unknown'}
              </span>
            </div>
          ))}
          {assignees.length > 2 && (
            <div className="text-muted" style={{ fontSize: '10px' }}>
              +{assignees.length - 2} more
            </div>
          )}
        </div>
      </td>
    );
  };

  const ActionCell = (props) => {
    return (
      <td>
        <div className="d-flex gap-1">
          <button
            className="btn btn-sm btn-info"
            onClick={() => setSelectedTarget(props.dataItem)}
            title="View Details"
          >
            <i className="fas fa-eye"></i>
          </button>
          <button
            className="btn btn-sm btn-warning"
            onClick={() => {
              setEditTarget(props.dataItem);
              setShowEditModal(true);
            }}
            title="Edit Target"
          >
            <i className="fas fa-edit"></i>
          </button>
        </div>
      </td>
    );
  };

  const DateCell = (props) => {
    const date = props.dataItem[props.field];
    return (
      <td>{date ? moment(date).format("DD-MM-YYYY") : "-"}</td>
    );
  };

  const AmountCell = (props) => {
    const targetType = props.dataItem.target_type || 'amount';
    const assignees = props.dataItem.assignees || [];

    if (targetType === 'number') {
      // For number targets, show sum of assigned_number from all assignees
      let totalAssignedNumber = 0;
      assignees.forEach(assignee => {
        totalAssignedNumber += Number(assignee.assigned_number) || 0;
      });

      return (
        <td className="text-end">
          {new Intl.NumberFormat('en-IN').format(totalAssignedNumber)}
        </td>
      );
    } else {
      // For amount targets, show sum of assigned_amount with currency
      let totalAssignedAmount = 0;
      assignees.forEach(assignee => {
        totalAssignedAmount += Number(assignee.assigned_amount) || 0;
      });

      const currencyCode = props.dataItem.currency || 'INR';
      const currencySymbol = CurrencyList.find(c => c.code === currencyCode)?.symbol || '₹';

      return (
        <td className="text-end">
          {totalAssignedAmount ? `${currencySymbol}${new Intl.NumberFormat('en-IN', {
            maximumFractionDigits: 0
          }).format(totalAssignedAmount)}` : "-"}
        </td>
      );
    }
  };

  const AchievementCell = (props) => {
    const targetType = props.dataItem.target_type || 'amount';
    const assignees = props.dataItem.assignees || [];

    if (targetType === 'number') {
      // For number targets, show sum of rnc_ach + rec_ach without currency
      let totalAchievement = 0;
      assignees.forEach(assignee => {
        totalAchievement += (Number(assignee.rnc_ach) || 0) + (Number(assignee.rec_ach) || 0);
      });

      return (
        <td className="text-end">
          {new Intl.NumberFormat('en-IN').format(totalAchievement)}
        </td>
      );
    } else {
      // For amount targets, show sum of rnc_ach + rec_ach with currency
      let totalAchievement = 0;
      assignees.forEach(assignee => {
        totalAchievement += (Number(assignee.rnc_ach) || 0) + (Number(assignee.rec_ach) || 0);
      });

      const currencyCode = props.dataItem.currency || 'INR';
      const currencySymbol = CurrencyList.find(c => c.code === currencyCode)?.symbol || '₹';

      return (
        <td className="text-end">
          {totalAchievement ? `${currencySymbol}${new Intl.NumberFormat('en-IN', {
            maximumFractionDigits: 0
          }).format(totalAchievement)}` : "-"}
        </td>
      );
    }
  };

  const PerformanceCell = (props) => {
    const targetType = props.dataItem.target_type || 'amount';
    const assignees = props.dataItem.assignees || [];
    let totalAchievement = 0;
    let targetValue = 0;

    // Calculate total achievement
    assignees.forEach(assignee => {
      totalAchievement += (Number(assignee.rnc_ach) || 0) + (Number(assignee.rec_ach) || 0);
    });

    // Get target value based on type
    if (targetType === 'number') {
      targetValue = props.dataItem.target_number || 0;
    } else {
      targetValue = props.dataItem.target_amount || 0;
    }

    const percentage = targetValue > 0 ? ((totalAchievement / targetValue) * 100).toFixed(1) : 0;

    const badgeClass = percentage >= 100 ? 'bg-success' :
      percentage >= 75 ? 'bg-warning' : 'bg-danger';

    return (
      <td className="text-center">
        <span className={`badge performance-badge ${badgeClass}`}>
          {percentage}%
        </span>
      </td>
    );
  };

  const processedData = process(filteredTargets, dataState);

  return (
    <div className="container-fluid">
      {/* Summary Cards */}
      <div className="row mb-4">
        <div className="col-md-6 mb-3 sales-targets-summary">
          <div className="card bg-primary text-white shadow-sm">
            <div className="card-body py-4">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h6 className="card-title mb-2 opacity-75">Total Company Target</h6>
                  <h3 className="mb-0 fw-bold">
                    ₹{new Intl.NumberFormat('en-IN').format(summary.total_target || 0)}
                  </h3>
                </div>
                <div className="text-end">
                  <i className="fas fa-bullseye fa-3x opacity-50"></i>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="col-md-6 mb-3 sales-targets-summary">
          <div className="card bg-success text-white shadow-sm">
            <div className="card-body py-4">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h6 className="card-title mb-2 opacity-75">Total Company Achievement</h6>
                  <h3 className="mb-0 fw-bold">
                    ₹{new Intl.NumberFormat('en-IN').format(summary.total_achievement || 0)}
                  </h3>
                </div>
                <div className="text-end">
                  <i className="fas fa-chart-line fa-3x opacity-50"></i>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-12">
          <div className="card">
            <div className="card-header d-flex justify-content-between align-items-center">
              <h5 className="mb-0">
                <i className="fas fa-bullseye me-2"></i>
                Sales Targets
              </h5>
              <button
                className="btn btn-success btn-sm"
                onClick={() => excelExportRef.current?.save()}
              >
                <i className="fas fa-file-excel me-2"></i>
                Export Excel
              </button>
            </div>

            <div className="card-body p-0">
              <div className="kendo-grid-wrapper">
                <ExcelExport
                  data={filteredTargets}
                  ref={excelExportRef}
                  fileName={`Sales_Targets_${moment().format('YYYY-MM-DD')}.xlsx`}
                >
                  <Grid
                    data={processedData}
                    {...dataState}
                    onDataStateChange={handleDataStateChange}
                    sortable={true}
                    filterable={true}
                    pageable={{
                      pageSizes: [5, 10, 20, 50],
                      buttonCount: 5
                    }}
                    resizable={true}
                    loading={loading}
                    style={{
                      width: '100%'
                    }}
                    className="custom-kendo-grid"
                  >
                    <GridColumn
                      field="title"
                      title="Target Title"
                      width="160px"
                      headerClassName="text-center custom-header"
                      headerStyle={{ textAlign: 'center' }}
                    />
                    <GridColumn
                      field="assignees"
                      title="Assignees"
                      width="180px"
                      filterable={false}
                      sortable={false}
                      cell={AssigneesCell}
                      headerClassName="text-center custom-header"
                      headerStyle={{ textAlign: 'center' }}
                    />
                    <GridColumn
                      field="start_date"
                      title="Start Date"
                      width="110px"
                      cell={DateCell}
                      headerClassName="text-center custom-header"
                      headerStyle={{ textAlign: 'center' }}
                    />
                    <GridColumn
                      field="end_date"
                      title="End Date"
                      width="110px"
                      cell={DateCell}
                      headerClassName="text-center custom-header"
                      headerStyle={{ textAlign: 'center' }}
                    />
                    <GridColumn
                      field="period"
                      title="Period"
                      width="90px"
                      headerClassName="text-center custom-header"
                      headerStyle={{ textAlign: 'center' }}
                    />
                    <GridColumn
                      field="target_amount"
                      title="Target Amount"
                      width="130px"
                      cell={AmountCell}
                      headerClassName="text-center custom-header"
                      headerStyle={{ textAlign: 'center' }}
                    />
                    <GridColumn
                      field="achievement"
                      title="Achievement"
                      width="130px"
                      cell={AchievementCell}
                      headerClassName="text-center custom-header"
                      headerStyle={{ textAlign: 'center' }}
                    />
                    <GridColumn
                      field="performance"
                      title="Performance"
                      width="110px"
                      filterable={false}
                      sortable={false}
                      cell={PerformanceCell}
                      headerClassName="text-center custom-header"
                      headerStyle={{ textAlign: 'center' }}
                    />
                    <GridColumn
                      title="Actions"
                      width="120px"
                      filterable={false}
                      sortable={false}
                      cell={ActionCell}
                      headerClassName="text-center custom-header"
                      headerStyle={{ textAlign: 'center' }}
                    />
                  </Grid>
                </ExcelExport>
              </div>
            </div>
          </div>
        </div>
      </div>

      <ChartTargets period={period} />

      {selectedTarget && (
        <TargetModal
          target={selectedTarget}
          onClose={() => {
            setSelectedTarget(null);
            fetchTargets();
          }}
        />
      )}

      {showEditModal && editTarget && (
        <EditTargetModal
          target={editTarget}
          show={showEditModal}
          onClose={() => {
            setShowEditModal(false);
            setEditTarget(null);
          }}
          onSuccess={() => {
            fetchTargets();
            setShowEditModal(false);
            setEditTarget(null);
          }}
        />
      )}
    </div>
  );
}
